import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const GREETING = "Ciao! Sono **Emmi**, l'assistente virtuale di Sdebitiamoci Srl. La consulenza è **gratuita** e paghi solo al successo. Per capire come aiutarti — qual è il tuo problema principale?";

const QUICK_CHIPS = [
  "🏠 Asta immobiliare",
  "📋 Pignoramento",
  "🏦 Difficoltà mutuo",
  "❓ Altro",
];

const EXTRA_CHIPS = [
  { label: "📄 Fac-simile contratto", msg: "Mi mandi il fac-simile del contratto?" },
];

// Renders **bold**, links [text](url) and preserves newlines
function renderMessage(text) {
  const parts = [];
  let idx = 0;
  const regex = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > idx) parts.push({ t: "text", v: text.slice(idx, m.index), k: `t${idx}` });
    if (m[2]) parts.push({ t: "b", v: m[2], k: `b${m.index}` });
    else parts.push({ t: "a", v: m[3], href: m[4], k: `a${m.index}` });
    idx = m.index + m[0].length;
  }
  if (idx < text.length) parts.push({ t: "text", v: text.slice(idx), k: `t${idx}` });
  return parts.map((p) => {
    if (p.t === "b") return <strong key={p.k}>{p.v}</strong>;
    if (p.t === "a") return (
      <a key={p.k} href={p.href} target="_blank" rel="noreferrer noopener" className="underline text-[#2d6fc4] font-medium">{p.v}</a>
    );
    return <span key={p.k} style={{ whiteSpace: "pre-wrap" }}>{p.v}</span>;
  });
}

function sessionId() {
  let sid = localStorage.getItem("chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("chat_sid", sid);
  }
  return sid;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    document.body.classList.toggle("chat-open", open);
    return () => document.body.classList.remove("chat-open");
  }, [open]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || streaming) return;
    setMessages(m => [...m, { id: crypto.randomUUID(), role: "user", content: text }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trimStart();
          if (raw === "[DONE]") continue;
          let payload;
          try {
            payload = JSON.parse(raw);
          } catch {
            payload = raw;
          }
          if (typeof payload === "string" && payload.startsWith("[ERRORE]")) {
            setMessages(m => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], content: "Mi spiace, c'è stato un problema. Riprova o scrivici su WhatsApp." };
              return c;
            });
            continue;
          }
          setMessages(m => {
            const c = [...m];
            c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + payload };
            return c;
          });
        }
      }
    } catch (e) {
      setMessages(m => {
        const c = [...m];
        c[c.length - 1] = { ...c[c.length - 1], content: "Problema di connessione. Prova più tardi o scrivici su WhatsApp." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(v => !v)}
        data-testid="chat-toggle"
        aria-label="Apri chat"
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
        className={`fixed bottom-6 right-6 z-40 h-14 rounded-full bg-[#14417B] text-[#FFFFFF] shadow-2xl flex items-center justify-center gap-2 hover:bg-[#1a4a8a] ${open ? "w-14" : "w-14 sm:w-auto sm:px-5"}`}
        style={{ transition: "background-color 250ms ease" }}
      >
        {open ? <X size={20}/> : (
          <>
            <MessageCircle size={22} strokeWidth={1.6}/>
            <span data-testid="chat-toggle-label" className="hidden sm:inline text-sm font-medium whitespace-nowrap">Chatta con noi</span>
          </>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-md h-[70vh] max-h-[600px] rounded-lg overflow-hidden border border-[#E5E7EB] shadow-2xl flex flex-col"
            style={{ background: "rgba(255, 255, 255, 0.94)", backdropFilter: "blur(24px)" }}
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center gap-3 bg-[#1a4a8a] text-[#FFFFFF]">
              <Sparkles size={16} className="text-white" strokeWidth={1.5}/>
              <div>
                <p className="font-serif text-lg leading-none">Emmi — Assistente Sdebitiamoci</p>
                <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-white mt-1">risposta in italiano · gratuita</p>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {messages.map((m, i) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                  <div className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1a4a8a] text-[#FFFFFF]" : "bg-[#F1F5F9] text-[#2d6fc4] border border-[#E5E7EB]"}`}>
                    {m.content ? renderMessage(m.content) : <span className="opacity-50 italic">…</span>}
                  </div>
                </div>
              ))}
              {messages.length === 1 && !streaming && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-quick-chips">
                  {QUICK_CHIPS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => send(c)}
                      data-testid={`chat-chip-${c.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB] bg-white text-[#2d6fc4] hover:border-[#14417B] hover:text-[#2d6fc4]"
                      style={{ transition: "border-color 200ms ease, color 200ms ease" }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <form onSubmit={e => { e.preventDefault(); send(); }} className="border-t border-[#E5E7EB] p-3 flex flex-col gap-2 bg-[#FFFFFF]">
              <div className="flex flex-wrap gap-1.5" data-testid="chat-extra-chips">
                {EXTRA_CHIPS.map(c => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => send(c.msg)}
                    disabled={streaming}
                    data-testid="chat-chip-contratto"
                    className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5E7EB] bg-[#F1F5F9] text-[#2d6fc4] hover:border-[#14417B] hover:bg-white disabled:opacity-40"
                    style={{ transition: "border-color 200ms ease, background-color 200ms ease" }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Scrivi la tua domanda…"
                  className="flex-1 bg-transparent px-3 py-3 text-sm focus:outline-none"
                  disabled={streaming}
                />
                <button type="submit" data-testid="chat-send" disabled={streaming || !input.trim()} className="btn-cta p-3 rounded-lg disabled:opacity-40">
                  <Send size={16}/>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
